// src/lib/aircraft-availability.js
//
// Single source of truth for "is this aircraft offered to customers / bookable".
//
// An aircraft is AVAILABLE iff:
//   (a) it is not manually hidden (aircraft.hidden !== true), AND
//   (b) at least one ACTIVE operator owns it — operator.status === 'active' and
//       the aircraft id is in that operator.fleet[] (the ownership array; aircraft
//       docs have NO operator reference, the link is operator.fleet[] -> aircraft).
//
// Aircraft with no operator at all (orphans) and aircraft whose only operators are
// inactive are BOTH treated as unavailable — there is no "showcase aircraft"
// exception. The SAME rule drives the customer fleet listing (display) AND the
// booking path (quote + bookings-create), so a hidden/orphaned aircraft is not
// just invisible, it cannot be booked.
//
// `operators` is a list of operator records that expose { status, fleet } where
// fleet is an array of human aircraft ids (the projection `fleet[]->id`). This is
// exactly the shape of operatorFleet[] in the customer bundle and of the small
// operator projections used by the booking endpoints.

import { getValidPolicyTiers } from './cancellation-policy-display.js';
import { aircraftHasBaggageCapacity } from './feasibility.js';

// Bookability gate (re-exported from the feasibility layer so it sits next to the
// no-policy / no-pricing rules): an aircraft with no maxLuggage set cannot have
// its baggage validated, so — exactly like an aircraft with no own pricing or no
// valid cancellation policy — it is not quotable to a fixed price (shown as
// "Price on request") and the booking path rejects it server-side. The funnel
// piece-count filter and the bookings-create backstop both need a slot budget;
// without one the aircraft is unsellable, not merely missing a bag count.
export { aircraftHasBaggageCapacity };

export const OPERATOR_ACTIVE_STATUS = 'active';

export function operatorIsActive(operator) {
  return !!operator && operator.status === OPERATOR_ACTIVE_STATUS;
}

export function operatorOwnsAircraft(operator, aircraftId) {
  return !!operator && Array.isArray(operator.fleet) && operator.fleet.includes(aircraftId);
}

/** True if any ACTIVE operator owns the aircraft (via operator.fleet[]). */
export function aircraftHasActiveOperator(aircraftId, operators) {
  if (!aircraftId || !Array.isArray(operators)) return false;
  return operators.some(o => operatorIsActive(o) && operatorOwnsAircraft(o, aircraftId));
}

/** True if the aircraft is not hidden AND has at least one active operator. */
export function isAircraftAvailable(aircraft, operators) {
  if (!aircraft || aircraft.hidden === true) return false;
  return aircraftHasActiveOperator(aircraft.id, operators);
}

// Bookability gate — an aircraft is only sellable if it carries a cancellation
// policy with at least one VALID time tier. Without it the refund-on-cancel is
// undefined (a flown flight could even refund in full — see booking/cancel.js),
// so such an aircraft is treated EXACTLY like an aircraft with no own pricing:
// it is not quotable to a fixed price (shown as "Price on request") and the
// booking path rejects it server-side. Reuses getValidPolicyTiers — the SAME
// single-source strict filter the display/email/manage surfaces use — so
// "is there a valid policy" can never diverge across surfaces. Accepts either
// projection field name: `policy` (customer bundle / quote) or `policyData`
// (bookings-create). Pure.
export function aircraftHasValidCancellationPolicy(aircraft) {
  if (!aircraft) return false;
  // Use a DEREFERENCED policy object (one that actually carries `tiers`). Some
  // projections expose `policyData` (bookings-create) while the spread `...` also
  // leaves the raw `policy` REFERENCE ({_ref} only, no tiers) on the doc — prefer
  // whichever candidate has a tiers array so the raw ref never shadows the data.
  for (const c of [aircraft.policyData, aircraft.policy]) {
    if (c && Array.isArray(c.tiers)) return getValidPolicyTiers(c).length > 0;
  }
  return false;
}

/** Keep only the aircraft that are available (display + booking share this). */
export function filterAvailableFleet(fleet, operators) {
  if (!Array.isArray(fleet)) return [];
  return fleet.filter(ac => isAircraftAvailable(ac, operators));
}
